/**
 * Hook for fetching live "All Time" awards data
 *
 * When viewing "All Time" on the Hall of Fame, this hook fetches live data
 * directly from the RPCs (like the Live Stats tab) rather than using stored
 * award snapshots. This provides real-time leaderboard data with W/D/L details.
 */

import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { headToHeadPct } from '../utils/winRate';
import { LiveAward, AwardsByCategory, AwardCategory, MedalType } from '../types/awards';
import { AWARD_CATEGORIES, AWARD_CATEGORY_ORDER } from '../constants/awards';
import {
  ChemistryPairResponse,
  RivalryLeaderboardResponse,
  TrioLeaderboardResponse,
  TRIO_K_VALUE,
} from '../types/chemistry';

// Helper to create a unique ID for awards.
// Pair/trio awards must pass all participant IDs — a player can appear as
// player1 in multiple top-ranked pairings (e.g. tied gold rivalries), so rank
// + player1 alone is not unique.
const createAwardId = (category: string, rank: number, ...participantIds: string[]) =>
  `live_${category}_${rank}_${participantIds.join('_')}`;

// Helper to get medal type from rank (1-indexed)
const getMedalType = (rank: number): MedalType => {
  switch (rank) {
    case 1: return 'gold';
    case 2: return 'silver';
    case 3: return 'bronze';
    default: return 'bronze';
  }
};

// Helper to assign ranks with ties (players with same value get same rank)
// Returns array of ranks corresponding to each item in the sorted array.
// The value may be a [score, games] key: rate awards rank a higher score first,
// then more games (the stronger evidence), and only entries level on both share
// a medal - the same order as calculate_awards (20261002_award_ties_shared_medals).
const sameKey = (a: number | number[], b: number | number[]) =>
  Array.isArray(a) && Array.isArray(b) ? a.length === b.length && a.every((v, i) => v === b[i]) : a === b;
/** Sort comparator: higher score first, then more games. */
const scoreThenGames = <T>(score: (x: T) => number, games: (x: T) => number) => (a: T, b: T) =>
  score(b) - score(a) || games(b) - games(a);
const assignRanksWithTies = <T>(items: T[], getValue: (item: T) => number | number[]): number[] => {
  if (items.length === 0) return [];

  const ranks: number[] = [];
  let currentRank = 1;

  for (let i = 0; i < items.length; i++) {
    if (i === 0) {
      ranks.push(currentRank);
    } else {
      const prevValue = getValue(items[i - 1]);
      const currValue = getValue(items[i]);
      // If same value as previous, same rank; otherwise increment
      if (sameKey(currValue, prevValue)) {
        ranks.push(ranks[i - 1]);
      } else {
        currentRank = i + 1; // Standard competition ranking (1, 2, 2, 4)
        ranks.push(currentRank);
      }
    }
  }

  return ranks;
};

// Points-based performance rate: W=3, D=1, L=0 as a % of points available.
const performanceRate = (games: number, wins: number, draws: number) =>
  games > 0 ? ((wins * 3 + draws) / (games * 3)) * 100 : 0;

/**
 * Cursed Duos: worst chemistry pairs. Curse score = (100 - performance rate) ×
 * games / (games + 10). Exported so the trophy cabinet can rank spoons per
 * season without loading every other award.
 */
export const rankCursedDuos = (data: ChemistryPairResponse[], depth = 3): LiveAward[] => {
  const CHEMISTRY_K = 10;
  const cursedPairs = data
    .filter((pair) => pair.player1_id < pair.player2_id) // Deduplicate
    .map((pair) => {
      const games = Number(pair.games_together);
      const rate = performanceRate(games, Number(pair.wins_together), Number(pair.draws_together));
      return { ...pair, curseScore: (100 - rate) * (games / (games + CHEMISTRY_K)), performanceRate: rate };
    })
    .sort(scoreThenGames((p) => p.curseScore, (p) => Number(p.games_together)));

  const ranks = assignRanksWithTies(cursedPairs, (p) => [p.curseScore, Number(p.games_together)]);
  return cursedPairs.map((pair, index): LiveAward => ({
    id: createAwardId('cursed_duos', ranks[index], pair.player1_id, pair.player2_id),
    playerId: pair.player1_id,
    playerName: pair.player1_name,
    partnerId: pair.player2_id,
    partnerName: pair.player2_name,
    category: 'cursed_duos',
    medalType: getMedalType(ranks[index]),
    rank: ranks[index],
    year: null,
    value: pair.curseScore,
    wins: Number(pair.wins_together),
    draws: Number(pair.draws_together),
    losses: Number(pair.losses_together),
    gamesTogether: Number(pair.games_together),
    winPercentage: pair.performanceRate,
    awardedAt: new Date().toISOString(),
  })).filter((a) => a.rank <= depth);
};

/** Cursed Trio: worst three-player chemistry, the same curse score with TRIO_K_VALUE. */
export const rankCursedTrios = (data: TrioLeaderboardResponse[], depth = 3): LiveAward[] => {
  const trios = data
    .map((trio) => {
      const games = Number(trio.games_together);
      const rate = performanceRate(games, Number(trio.wins), Number(trio.draws));
      return { ...trio, curseScore: (100 - rate) * (games / (games + TRIO_K_VALUE)), performanceRate: rate };
    })
    .sort(scoreThenGames((t) => t.curseScore, (t) => Number(t.games_together)));

  const ranks = assignRanksWithTies(trios, (t) => [t.curseScore, Number(t.games_together)]);
  return trios.map((trio, index): LiveAward => ({
    id: createAwardId('cursed_trio', ranks[index], trio.player1_id, trio.player2_id, trio.player3_id),
    playerId: trio.player1_id,
    playerName: trio.player1_name,
    partnerId: trio.player2_id,
    partnerName: trio.player2_name,
    partner2Id: trio.player3_id,
    partner2Name: trio.player3_name,
    category: 'cursed_trio',
    medalType: getMedalType(ranks[index]),
    rank: ranks[index],
    year: null,
    value: trio.curseScore,
    wins: Number(trio.wins),
    draws: Number(trio.draws),
    losses: Number(trio.losses),
    gamesTogether: Number(trio.games_together),
    winPercentage: trio.performanceRate,
    awardedAt: new Date().toISOString(),
  })).filter((a) => a.rank <= depth);
};

// Helper to group awards by category and deduplicate pair/trio awards
// placeholders: map of categoryId to placeholder message for categories without data
const groupLiveAwardsByCategory = (
  awards: LiveAward[],
  placeholders: Map<AwardCategory, string> = new Map()
): AwardsByCategory[] => {
  const grouped: AwardsByCategory[] = [];

  for (const categoryId of AWARD_CATEGORY_ORDER) {
    let categoryAwards = awards.filter(a => a.category === categoryId);
    const config = AWARD_CATEGORIES[categoryId];

    // Deduplicate pair awards (keep one entry per unique pair per medal)
    if (config.isPairAward) {
      const seen = new Set<string>();
      categoryAwards = categoryAwards.filter(award => {
        const ids = [award.playerId, award.partnerId].sort();
        const key = `${award.medalType}_${ids.join('_')}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    // Deduplicate trio awards (keep one entry per unique trio per medal)
    if (config.isTrioAward) {
      const seen = new Set<string>();
      categoryAwards = categoryAwards.filter(award => {
        const ids = [award.playerId, award.partnerId, award.partner2Id].sort();
        const key = `${award.medalType}_${ids.join('_')}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    const placeholderMessage = placeholders.get(categoryId);

    // Include category if it has awards OR has a placeholder message
    if (categoryAwards.length > 0 || placeholderMessage) {
      grouped.push({
        category: categoryId,
        config: config,
        awards: categoryAwards.sort((a, b) => {
          const medalOrder = { gold: 0, silver: 1, bronze: 2 };
          return medalOrder[a.medalType] - medalOrder[b.medalType];
        }),
        placeholderMessage: categoryAwards.length === 0 ? placeholderMessage : undefined,
      });
    }
  }

  return grouped;
};

/**
 * Hook to fetch live awards for display
 *
 * @param yearFilter - 'all' for all-time, a specific year number, or null to skip loading
 * @param depth - how many places per category (3 for medals; more for a live race)
 */
export const useLiveAwards = (yearFilter: 'all' | number | null = 'all', depth = 3) => {
  const [awards, setAwards] = useState<LiveAward[]>([]);
  const [awardsByCategory, setAwardsByCategory] = useState<AwardsByCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Convert year filter to format expected by RPCs (null = all time)
  const targetYear = yearFilter === 'all' ? null : yearFilter;

  useEffect(() => {
    if (yearFilter === null) {
      setAwards([]);
      setAwardsByCategory([]);
      setLoading(false);
      return;
    }
    const fetchLiveAwards = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all data in parallel for performance
        const [
          xpResult,
          winRatesResult,
          goalDiffsResult,
          attendanceStreaksResult,
          winStreaksResult,
          unbeatenStreaksResult,
          capsResult,
          chemistryResult,
          buddiesResult,
          rivalryResult,
          trioResult,
          cursedTrioResult,
          teamColorsResult,
        ] = await Promise.all([
          // XP Champion - from highest_xp_records_view (includes snapshot_date)
          // Uses xp_v2 for current era, falls back to xp for v1 era
          supabase
            .from('highest_xp_records_view')
            .select('player_id, friendly_name, xp, xp_v2, snapshot_date')
            .order('xp_v2', { ascending: false, nullsFirst: false })
            .limit(depth),

          // Win Rate Leader
          supabase.rpc('get_player_win_rates', { target_year: targetYear }),

          // Net Positive (goal differentials)
          supabase.rpc('get_player_goal_differentials', { target_year: targetYear }),

          // Iron Man (attendance streaks)
          supabase.rpc('get_player_attendance_streaks', { target_year: targetYear }),

          // Hot Streak (winning streaks)
          supabase.rpc('get_player_winning_streaks', { target_year: targetYear }),

          // The Wall (unbeaten streaks)
          supabase.rpc('get_player_unbeaten_streaks', { target_year: targetYear }),

          // Appearance King (caps)
          supabase.rpc('get_player_caps', { target_year: targetYear }),

          // Dynamic Duo (chemistry)
          supabase.rpc('get_player_chemistry', { target_player_id: null, target_year: targetYear }),

          // Best Buddies
          supabase.rpc('get_best_buddies', { target_year: targetYear }),

          // Fiercest Rivalry
          supabase.rpc('get_rivalry_leaderboard', { limit_count: depth, target_year: targetYear }),

          // Dream Team Trio
          supabase.rpc('get_trio_leaderboard', { limit_count: depth, target_year: targetYear, sort_order: 'best' }),

          // Cursed Trio
          supabase.rpc('get_trio_leaderboard', { limit_count: depth, target_year: targetYear, sort_order: 'worst' }),

          // Team Colors (for Blue Blood and Dutch Master)
          supabase.rpc('get_player_team_colors', { target_year: targetYear }),
        ]);

        const allAwards: LiveAward[] = [];
        // Track placeholder messages for categories that don't meet thresholds
        const placeholders = new Map<AwardCategory, string>();

        // Process XP Champion
        if (xpResult.data) {
          const sorted = xpResult.data.slice(0, depth);
          const ranks = assignRanksWithTies(sorted, (p: any) => p.xp_v2 ?? p.xp ?? 0);
          sorted.forEach((player: any, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (ranks[index] > depth) return;
            allAwards.push({
              id: createAwardId('xp_champion', ranks[index], player.player_id),
              playerId: player.player_id,
              playerName: player.friendly_name || 'Unknown',
              category: 'xp_champion',
              medalType: getMedalType(ranks[index]),
              rank: ranks[index],
              year: null,
              value: player.xp_v2 ?? player.xp ?? 0,
              achievedDate: player.snapshot_date,
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process Win Rate Leader
        if (winRatesResult.data) {
          // Filter out players who don't meet minimum games threshold (win_rate is null)
          const sorted = winRatesResult.data
            .filter((p: any) => p.win_rate !== null)
            .sort(scoreThenGames((p: any) => Number(p.win_rate), (p: any) => Number(p.total_games)));

          if (sorted.length === 0) {
            // No one meets the 10-game minimum threshold yet
            placeholders.set('win_rate_leader', 'Check back after 10 even-team games have been played');
          } else {
            const ranks = assignRanksWithTies(sorted, (p: any) => [Number(p.win_rate), Number(p.total_games)]);
            sorted.forEach((player: any, index: number) => {
              // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
              if (ranks[index] > depth) return;
              allAwards.push({
                id: createAwardId('win_rate_leader', ranks[index], player.id),
                playerId: player.id,
                playerName: player.friendly_name,
                category: 'win_rate_leader',
                medalType: getMedalType(ranks[index]),
                rank: ranks[index],
                year: null,
                value: Number(player.win_rate),
                wins: Number(player.wins),
                draws: Number(player.draws),
                losses: Number(player.losses),
                gamesTogether: Number(player.total_games),
                awardedAt: new Date().toISOString(),
              });
            });
          }
        }

        // Process Net Positive (goal differentials)
        if (goalDiffsResult.data) {
          const sorted = goalDiffsResult.data
            .sort((a: any, b: any) => Number(b.goal_differential) - Number(a.goal_differential));
          const ranks = assignRanksWithTies(sorted, (p: any) => Number(p.goal_differential));
          sorted.forEach((player: any, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (ranks[index] > depth) return;
            allAwards.push({
              id: createAwardId('net_positive', ranks[index], player.id),
              playerId: player.id,
              playerName: player.friendly_name,
              category: 'net_positive',
              medalType: getMedalType(ranks[index]),
              rank: ranks[index],
              year: null,
              value: Number(player.goal_differential),
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process Iron Man (attendance streaks)
        if (attendanceStreaksResult.data) {
          const sorted = attendanceStreaksResult.data
            .sort((a: any, b: any) => Number(b.max_streak) - Number(a.max_streak));
          const ranks = assignRanksWithTies(sorted, (p: any) => Number(p.max_streak));
          sorted.forEach((player: any, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (ranks[index] > depth) return;
            allAwards.push({
              id: createAwardId('iron_man', ranks[index], player.id),
              playerId: player.id,
              playerName: player.friendly_name,
              category: 'iron_man',
              medalType: getMedalType(ranks[index]),
              rank: ranks[index],
              year: null,
              value: Number(player.max_streak),
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process Hot Streak (winning streaks)
        if (winStreaksResult.data) {
          const sorted = winStreaksResult.data
            .sort((a: any, b: any) => Number(b.max_win_streak) - Number(a.max_win_streak));
          const ranks = assignRanksWithTies(sorted, (p: any) => Number(p.max_win_streak));
          sorted.forEach((player: any, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (ranks[index] > depth) return;
            allAwards.push({
              id: createAwardId('hot_streak', ranks[index], player.id),
              playerId: player.id,
              playerName: player.friendly_name,
              category: 'hot_streak',
              medalType: getMedalType(ranks[index]),
              rank: ranks[index],
              year: null,
              value: Number(player.max_win_streak),
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process The Wall (unbeaten streaks)
        if (unbeatenStreaksResult.data) {
          const sorted = unbeatenStreaksResult.data
            .sort((a: any, b: any) => Number(b.max_unbeaten_streak) - Number(a.max_unbeaten_streak));
          const ranks = assignRanksWithTies(sorted, (p: any) => Number(p.max_unbeaten_streak));
          sorted.forEach((player: any, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (ranks[index] > depth) return;
            allAwards.push({
              id: createAwardId('the_wall', ranks[index], player.id),
              playerId: player.id,
              playerName: player.friendly_name,
              category: 'the_wall',
              medalType: getMedalType(ranks[index]),
              rank: ranks[index],
              year: null,
              value: Number(player.max_unbeaten_streak),
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process Appearance King (caps)
        if (capsResult.data) {
          const sorted = capsResult.data
            .sort((a: any, b: any) => Number(b.total_games) - Number(a.total_games));
          const ranks = assignRanksWithTies(sorted, (p: any) => Number(p.total_games));
          sorted.forEach((player: any, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (ranks[index] > depth) return;
            allAwards.push({
              id: createAwardId('appearance_king', ranks[index], player.id),
              playerId: player.id,
              playerName: player.friendly_name,
              category: 'appearance_king',
              medalType: getMedalType(ranks[index]),
              rank: ranks[index],
              year: null,
              value: Number(player.total_games),
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process Dynamic Duo (chemistry)
        if (chemistryResult.data) {
          // Recalculate using points system to ensure consistency:
          // - Win = 3 points, Draw = 1 point, Loss = 0 points
          // - Performance rate = (wins×3 + draws×1) / (games×3) × 100
          // - Chemistry score = performance_rate × confidence_factor
          const CHEMISTRY_K = 10;
          const pairs = (chemistryResult.data as ChemistryPairResponse[])
            .filter((pair) => pair.player1_id < pair.player2_id) // Deduplicate
            .map(pair => {
              const games = Number(pair.games_together);
              const wins = Number(pair.wins_together);
              const draws = Number(pair.draws_together);

              // Calculate performance rate using points system
              const pointsEarned = (wins * 3) + (draws * 1);
              const pointsAvailable = games * 3;
              const performanceRate = pointsAvailable > 0 ? (pointsEarned / pointsAvailable) * 100 : 0;

              // Confidence factor based on sample size
              const confidenceFactor = games / (games + CHEMISTRY_K);
              const chemistryScore = performanceRate * confidenceFactor;

              return { ...pair, chemistryScore, performanceRate };
            })
            .sort(scoreThenGames((p) => p.chemistryScore, (p) => Number(p.games_together)));

          const pairRanks = assignRanksWithTies(pairs, (p: any) => [p.chemistryScore, Number(p.games_together)]);
          pairs.forEach((pair, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (pairRanks[index] > depth) return;
            allAwards.push({
              id: createAwardId('dynamic_duo', pairRanks[index], pair.player1_id, pair.player2_id),
              playerId: pair.player1_id,
              playerName: pair.player1_name,
              partnerId: pair.player2_id,
              partnerName: pair.player2_name,
              category: 'dynamic_duo',
              medalType: getMedalType(pairRanks[index]),
              rank: pairRanks[index],
              year: null,
              value: pair.chemistryScore,
              wins: Number(pair.wins_together),
              draws: Number(pair.draws_together),
              losses: Number(pair.losses_together),
              gamesTogether: Number(pair.games_together),
              winPercentage: pair.performanceRate,
              awardedAt: new Date().toISOString(),
            });
          });

          // Process Cursed Duos (worst chemistry)
          allAwards.push(...rankCursedDuos(chemistryResult.data as ChemistryPairResponse[], depth));
        }

        // Process Best Buddies
        if (buddiesResult.data) {
          // Deduplicate and sort by games together
          const buddies = buddiesResult.data
            .filter((buddy: any) => buddy.id < buddy.buddy_id) // Deduplicate
            .sort((a: any, b: any) => Number(b.games_together) - Number(a.games_together));

          const buddyRanks = assignRanksWithTies(buddies, (b: any) => Number(b.games_together));
          buddies.forEach((buddy: any, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (buddyRanks[index] > depth) return;
            allAwards.push({
              id: createAwardId('best_buddies', buddyRanks[index], buddy.id, buddy.buddy_id),
              playerId: buddy.id,
              playerName: buddy.friendly_name,
              partnerId: buddy.buddy_id,
              partnerName: buddy.buddy_friendly_name,
              category: 'best_buddies',
              medalType: getMedalType(buddyRanks[index]),
              rank: buddyRanks[index],
              year: null,
              value: Number(buddy.games_together),
              gamesTogether: Number(buddy.games_together),
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process Fiercest Rivalry
        if (rivalryResult.data) {
          // Head-to-head, the same as the stored medal (calculate_awards):
          // - Share of the decided games: W / (W + L) × 100 (draws left out,
          //   so 50% genuinely means level)
          // - Ranked by dominance = |share - 50|
          const rivalriesWithScore = (rivalryResult.data as RivalryLeaderboardResponse[])
            .map(rivalry => {
              const performanceRate = headToHeadPct(Number(rivalry.player1_wins), Number(rivalry.player2_wins));
              const rivalryScore = Math.abs(performanceRate - 50);
              return { ...rivalry, rivalryScore, performanceRate };
            })
            .sort(scoreThenGames((r) => r.rivalryScore, (r) => Number(r.games_against)));

          const rivalryRanks = assignRanksWithTies(rivalriesWithScore, (r: any) => [r.rivalryScore, Number(r.games_against)]);
          rivalriesWithScore.forEach((rivalry, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (rivalryRanks[index] > depth) return;
            allAwards.push({
              id: createAwardId('fiercest_rivalry', rivalryRanks[index], rivalry.player1_id, rivalry.player2_id),
              playerId: rivalry.player1_id,
              playerName: rivalry.player1_name,
              partnerId: rivalry.player2_id,
              partnerName: rivalry.player2_name,
              category: 'fiercest_rivalry',
              medalType: getMedalType(rivalryRanks[index]),
              rank: rivalryRanks[index],
              year: null,
              value: rivalry.rivalryScore, // Dominance: |head-to-head share - 50|
              wins: Number(rivalry.player1_wins),
              draws: Number(rivalry.draws),
              losses: Number(rivalry.player2_wins),
              gamesAgainst: Number(rivalry.games_against),
              winPercentage: rivalry.performanceRate, // Head-to-head share W / (W + L)
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process Dream Team Trio
        if (trioResult.data) {
          // Recalculate using points system to ensure consistency:
          // - Win = 3 points, Draw = 1 point, Loss = 0 points
          // - Performance rate = (wins×3 + draws×1) / (games×3) × 100
          // - Trio score = performance_rate × confidence_factor
          const triosWithScore = (trioResult.data as TrioLeaderboardResponse[])
            .map(trio => {
              const games = Number(trio.games_together);
              const wins = Number(trio.wins);
              const draws = Number(trio.draws);

              // Calculate performance rate using points system
              const pointsEarned = (wins * 3) + (draws * 1);
              const pointsAvailable = games * 3;
              const performanceRate = pointsAvailable > 0 ? (pointsEarned / pointsAvailable) * 100 : 0;

              // Confidence factor based on sample size
              const confidenceFactor = games / (games + TRIO_K_VALUE);
              const trioScore = performanceRate * confidenceFactor;

              return { ...trio, trioScore, performanceRate };
            })
            .sort(scoreThenGames((t) => t.trioScore, (t) => Number(t.games_together)));

          const trioRanks = assignRanksWithTies(triosWithScore, (t: any) => [t.trioScore, Number(t.games_together)]);
          triosWithScore.forEach((trio, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (trioRanks[index] > depth) return;
            allAwards.push({
              id: createAwardId('dream_team_trio', trioRanks[index], trio.player1_id, trio.player2_id, trio.player3_id),
              playerId: trio.player1_id,
              playerName: trio.player1_name,
              partnerId: trio.player2_id,
              partnerName: trio.player2_name,
              partner2Id: trio.player3_id,
              partner2Name: trio.player3_name,
              category: 'dream_team_trio',
              medalType: getMedalType(trioRanks[index]),
              rank: trioRanks[index],
              year: null,
              value: trio.trioScore,
              wins: Number(trio.wins),
              draws: Number(trio.draws),
              losses: Number(trio.losses),
              gamesTogether: Number(trio.games_together),
              winPercentage: trio.performanceRate,
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process Cursed Trio (worst trio chemistry)
        if (cursedTrioResult.data) {
          allAwards.push(...rankCursedTrios(cursedTrioResult.data as TrioLeaderboardResponse[], depth));
        }

        // Process Blue Blood and Dutch Master (team colors)
        if (teamColorsResult.data) {
          const blueTeam = teamColorsResult.data
            .filter((p: any) => p.team === 'blue')
            .sort(scoreThenGames((p: any) => Number(p.team_frequency), (p: any) => Number(p.caps)));

          const blueRanks = assignRanksWithTies(blueTeam, (p: any) => [Number(p.team_frequency), Number(p.caps)]);
          blueTeam.forEach((player: any, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (blueRanks[index] > depth) return;
            allAwards.push({
              id: createAwardId('blue_blood', blueRanks[index], player.id),
              playerId: player.id,
              playerName: player.friendly_name,
              category: 'blue_blood',
              medalType: getMedalType(blueRanks[index]),
              rank: blueRanks[index],
              year: null,
              value: Number(player.team_frequency) * 100, // Convert to percentage
              awardedAt: new Date().toISOString(),
            });
          });

          const orangeTeam = teamColorsResult.data
            .filter((p: any) => p.team === 'orange')
            .sort(scoreThenGames((p: any) => Number(p.team_frequency), (p: any) => Number(p.caps)));

          const orangeRanks = assignRanksWithTies(orangeTeam, (p: any) => [Number(p.team_frequency), Number(p.caps)]);
          orangeTeam.forEach((player: any, index: number) => {
            // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
            if (orangeRanks[index] > depth) return;
            allAwards.push({
              id: createAwardId('dutch_master', orangeRanks[index], player.id),
              playerId: player.id,
              playerName: player.friendly_name,
              category: 'dutch_master',
              medalType: getMedalType(orangeRanks[index]),
              rank: orangeRanks[index],
              year: null,
              value: Number(player.team_frequency) * 100, // Convert to percentage
              awardedAt: new Date().toISOString(),
            });
          });
        }

        // Process Super Sub (reserve appearances) — no RPC exists, always
        // query game_registrations directly for reserve counts.
        {
          const { data: reserveData } = await supabase
            .from('game_registrations')
            .select(`
              player_id,
              players!inner(friendly_name)
            `)
            .eq('status', 'reserve');

          if (reserveData) {
            // Group by player and count
            const counts = new Map<string, { player_id: string; friendly_name: string; count: number }>();
            reserveData.forEach((reg: any) => {
              const existing = counts.get(reg.player_id);
              if (existing) {
                existing.count++;
              } else {
                counts.set(reg.player_id, {
                  player_id: reg.player_id,
                  friendly_name: reg.players?.friendly_name || 'Unknown',
                  count: 1,
                });
              }
            });

            // Sort by count and get top 3
            const sorted = Array.from(counts.values())
              .sort((a, b) => b.count - a.count);

            const fallbackRanks = assignRanksWithTies(sorted, (p: any) => p.count);
            sorted.forEach((player: any, index: number) => {
              // Everyone level with the last medal place shares it (sorted in full, not cut at depth).
              if (fallbackRanks[index] > depth) return;
              allAwards.push({
                id: createAwardId('super_sub', fallbackRanks[index], player.player_id),
                playerId: player.player_id,
                playerName: player.friendly_name,
                category: 'super_sub',
                medalType: getMedalType(fallbackRanks[index]),
                rank: fallbackRanks[index],
                year: null,
                value: player.count,
                awardedAt: new Date().toISOString(),
              });
            });
          }
        }

        setAwards(allAwards);
        setAwardsByCategory(groupLiveAwardsByCategory(allAwards, placeholders));
      } catch (err) {
        console.error('Error fetching live awards:', err);
        setError('Failed to load live awards');
      } finally {
        setLoading(false);
      }
    };

    fetchLiveAwards();
    // yearFilter only matters for null (skip); targetYear covers the rest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetYear, depth, yearFilter === null]);

  return {
    awards,
    awardsByCategory,
    loading,
    error,
  };
};

export default useLiveAwards;
